import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { documentDetailPath, documentFilePath, useApi } from "../api/client";
import type { DocumentDetail as Detail } from "../api/types";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { DocumentViewer } from "../components/DocumentViewer";
import { TokenBreakdown } from "../components/TokenBreakdown";
import { DocumentHistory } from "../components/DocumentHistory";
import { RerunButton } from "../components/RerunButton";
import { VerifyFields } from "../components/VerifyFields";
import { Empty, ErrorState, Loading } from "../components/States";
import {
  EM_DASH,
  fmtCost,
  fmtMs,
  fmtTokens,
  fmtValue,
  humanise,
} from "../lib/format";

type Tab =
  | "fields"
  | "document"
  | "raw"
  | "tokens"
  | "verdict"
  | "history"
  | "verify";

const TABS: { key: Tab; label: string }[] = [
  { key: "fields", label: "Extracted fields" },
  { key: "document", label: "Original document" },
  { key: "raw", label: "Raw OCR" },
  { key: "tokens", label: "Tokens & cost" },
  { key: "verdict", label: "Verdict" },
  // Before Verify: a reviewer checks whether re-running fixed it before
  // spending time recording what it should have said.
  { key: "history", label: "History" },
  // Last, because it is the one tab a reader arrives at having already
  // decided something is wrong on the tabs before it.
  { key: "verify", label: "Verify" },
];

export function DocumentDetail() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const [compare, setCompare] = useState(false);
  const { data, error, loading, reload } = useApi<Detail>(
    id ? documentDetailPath(id) : null,
  );

  /*
   * The tab lives in the URL rather than in state, so the agent's field
   * drill-down can link straight to the part of this page that answers the
   * question it was opened with: ?tab=document for the scan, or
   * ?tab=fields&field=<name> to land on one row of the field list.
   */
  const tabParam = params.get("tab");
  const tab: Tab = TABS.some((t) => t.key === tabParam)
    ? (tabParam as Tab)
    : "fields";
  const highlight = params.get("field");

  const setTab = (next: Tab) => {
    const q = new URLSearchParams(params);
    // "fields" is the default, so it needs no parameter — and dropping it keeps
    // the plain document URL clean when a reader tabs back to it.
    if (next === "fields") q.delete("tab");
    else q.set("tab", next);
    setParams(q, { replace: true });
  };

  if (loading)
    return (
      <div className="card">
        <Loading what="document" />
      </div>
    );
  if (error)
    return (
      <div className="card">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  if (!data) return null;

  const dedup = Boolean(
    (data.stage_timings as Record<string, unknown> | null)?.deduplicated,
  );
  const gate = (data.extracted_data?._confidence_gate ?? null) as Record<
    string,
    unknown
  > | null;

  const rawGate = gate?.confidence_score;
  const gateScore =
    typeof rawGate === "number"
      ? rawGate
      : typeof rawGate === "string" &&
          rawGate.trim() !== "" &&
          Number.isFinite(Number(rawGate))
        ? Number(rawGate)
        : null;

  return (
    <>
      <p className="page-sub" style={{ marginBottom: 8 }}>
        <Link to="/documents">← Documents</Link>
      </p>

      <div className="page-head">
        <h1 className="page-title">
          {data.original_filename ?? data.document_id}
        </h1>
        {/* At the top, next to the document it acts on: the decision to re-run
            is made while looking at the extraction that disappointed the
            reader, not after finding a tab. */}
        <RerunButton id={id} onQueued={reload} />
      </div>
      <p className="page-sub">
        {humanise(data.extracted_data?.document_type as string | undefined)} ·{" "}
        <span className="mono">{data.document_id}</span>
        {data.application_id ? (
          <>
            {" "}
            ·{" "}
            <Link to={`/applications/${data.application_id}`}>application</Link>
          </>
        ) : null}
      </p>

      <div className="tiles">
        {/* Both numbers, each under its own name.

            They are different measurements and they diverge systematically: the
            gate is 40% document quality plus 60% a field-presence checklist, so
            two non-empty fields earn a flat 60 points and land almost every
            document in the 80s, while the agent's own confidence on the same
            documents sits in the 50s and 60s. Showing one and calling it "the"
            confidence is what made this page disagree with the list. */}
        <div className="tile">
          <div className="tile-label">Agent confidence</div>
          <div className="tile-value">
            <ConfidenceBadge
              value={data.agent_confidence ?? data.confidence}
              absentReason="No agent confidence was recorded for this document"
            />
          </div>
          <div className="tile-note">
            {data.agent_confidence === null || data.agent_confidence === undefined
              ? data.confidence === null
                ? "no confidence recorded"
                : "from the OCR result — no agent extraction on file"
              : data.model_used
                ? `self-reported by ${data.model_used}`
                : "the model's own self-report"}
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">
            {gateScore !== null ? "Confidence gate" : "Document confidence"}
          </div>
          <div className="tile-value">
            {/* The gate score is the number the routing decision was actually
                made on, which is why it keeps its own tile rather than being
                folded into the one above. */}
            <ConfidenceBadge
              value={gateScore !== null ? gateScore : data.confidence}
            />
          </div>
          <div className="tile-note">
            {gate?.gate_action ? humanise(String(gate.gate_action)) : null}
            {gate?.passed_deterministic_checks
              ? ` · checks ${gate.passed_deterministic_checks}`
              : null}
            {gateScore === null && data.confidence === null
              ? "no confidence recorded for this document"
              : null}
          </div>
        </div>
        {/* The third number, and the only one that says what to fix. The other
            two are scores; this one names the fields that were missing. */}
        <div className="tile">
          <div className="tile-label">Required fields</div>
          <div className="tile-value">
            {data.required_score === null ? (
              <span className="muted">{EM_DASH}</span>
            ) : (
              <span
                className={
                  data.required_score === 100
                    ? "req-ok"
                    : data.required_score >= 60
                      ? "req-warn"
                      : "req-bad"
                }
              >
                {data.required_score}%
              </span>
            )}
          </div>
          <div className="tile-note">
            {data.required_score === null
              ? data.agent_confidence === null
                ? "no agent extraction on this document"
                : "no fields are scored for this agent yet"
              : data.required_missing.length === 0
                ? `all ${data.required_total} scored fields extracted`
                : `${data.required_met} of ${data.required_total} · missing ${data.required_missing.join(", ")}`}
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">Tokens</div>
          <div className="tile-value">
            {fmtTokens(data.token_usage?.total_tokens ?? 0)}
          </div>
          {dedup ? (
            <div className="tile-note">this run was cached — 0 spent</div>
          ) : null}
        </div>
        <div className="tile">
          <div className="tile-label">Cost</div>
          <div className="tile-value">
            {fmtCost(data.token_usage?.estimated_cost_usd ?? null)}
          </div>
          {data.token_usage && !data.token_usage.cost_complete ? (
            <div className="tile-note">
              partial — a stage has no rate on file
            </div>
          ) : null}
        </div>
        <div className="tile">
          <div className="tile-label">OCR engine</div>
          <div className="tile-value" style={{ fontSize: 18 }}>
            {data.ocr_engine ?? EM_DASH}
          </div>
          {/* This is the OCR stage's own timing, and it was the only duration
              on the page -- which read as "the document took 31s" when it had
              actually taken 58. Labelled, with the end-to-end figure beside
              it. */}
          <div className="tile-note">
            {fmtMs(data.execution_time_ms)} in OCR
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">Processing time</div>
          <div className="tile-value" style={{ fontSize: 18 }}>
            {data.processing_ms ? fmtMs(data.processing_ms) : EM_DASH}
          </div>
          <div className="tile-note">
            {/* Named for the attempt, not the document: on a re-run the two are
                days apart, and the tile used to report the gap. */}
            last run: queued to finished
          </div>
        </div>
      </div>

      <ScoredFieldsCheck data={data} />

      {dedup ? (
        <div className="notice">
          This result was served from the deduplication cache, so this request
          consumed no tokens. The figures below are what the original processing
          of this file cost.
        </div>
      ) : null}

      <div className="card">
        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              className="tab"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="card-body">
          {tab === "fields" ? (
            <FieldsTab
              data={data}
              compare={compare}
              onToggleCompare={() => setCompare((v) => !v)}
              highlight={highlight}
              onClearHighlight={() => {
                const q = new URLSearchParams(params);
                q.delete("field");
                setParams(q, { replace: true });
              }}
            />
          ) : null}
          {tab === "document" ? (
            <DocumentViewer
              src={documentFilePath(data.document_id)}
              filename={data.original_filename}
            />
          ) : null}
          {tab === "raw" ? <RawTab data={data} /> : null}
          {tab === "tokens" ? (
            <TokenBreakdown usage={data.token_usage} />
          ) : null}
          {tab === "verdict" ? <VerdictTab data={data} gate={gate} /> : null}
          {tab === "history" ? <DocumentHistory id={id} /> : null}
          {tab === "verify" ? <VerifyFields id={id} /> : null}
        </div>
      </div>
    </>
  );
}

/**
 * The fields this document's score was calculated from, one row each.
 *
 * The tile above gives the percentage; this says what produced it. Without it
 * "80%" is a number a reviewer has to go and reconstruct — which five fields
 * were counted, which one was missing, and what the agent actually read for the
 * four that passed.
 *
 * Shown for every document, not only failing ones. A reviewer checking a 100%
 * still needs to see which five fields that was, because the answer changes
 * whenever someone edits the agent's selection.
 */
function ScoredFieldsCheck({ data }: { data: Detail }) {
  const rows = data.required_detail ?? [];
  const type = (data.extracted_data?.document_type as string) ?? null;

  if (data.required_score === null || rows.length === 0) {
    return (
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Scored fields</span>
        </div>
        <div className="card-body">
          <Empty
            title={
              data.agent_confidence === null
                ? "No agent extraction on this document"
                : "No fields are scored for this agent yet"
            }
            hint={
              data.agent_confidence === null
                ? "This document has no agent result, so there is nothing to check against."
                : "Choose the fields this agent is scored on from its Fields tab, and this document will be checked against them."
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head bordered">
        <span className="card-title">Scored fields</span>
        <span className="muted">
          {data.required_met} of {data.required_total} · {data.required_score}%
          {type ? (
            <>
              {" · "}
              {/* Straight to the switches that decide this list, because the
                  first question after seeing a score is usually "why is that
                  field counted?" */}
              <Link to={`/agents/${encodeURIComponent(type)}?tab=fields`}>
                change what counts
              </Link>
            </>
          ) : null}
        </span>
      </div>
      <div className="card-body">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col" style={{ width: 90 }}>
                  Extracted
                </th>
                <th scope="col">Field</th>
                <th scope="col">Value read from this document</th>
                <th scope="col">Rule</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.field}>
                  <td>
                    <span
                      className={`pill ${f.satisfied ? "pill-good" : "pill-critical"}`}
                    >
                      {f.satisfied ? "yes" : "no"}
                    </span>
                  </td>
                  <td>
                    <span className="mono">{f.field}</span>
                    {/* Only when it differs: saying "matched trade_name" under
                        trade_name would be noise on every row. */}
                    {f.matched && f.matched !== f.field ? (
                      <div className="muted" style={{ fontSize: 11 }}>
                        matched as <span className="mono">{f.matched}</span>
                      </div>
                    ) : null}
                  </td>
                  <td className="value-cell">
                    {f.value ? (
                      f.value
                    ) : (
                      <span className="muted">
                        {f.satisfied ? "—" : "nothing extracted"}
                      </span>
                    )}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {f.min_length > 1 ? `at least ${f.min_length} characters` : "any value"}
                    {f.aliases.length ? (
                      <div style={{ fontSize: 11 }}>
                        or {f.aliases.map((a) => a).join(", ")}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Per-field values with the model's own confidence.
 *
 * `compare` splits the view and puts the original document alongside, which is
 * the point of the whole screen: a confidence score tells a reviewer how sure
 * the model was, not whether it was right. Only the page itself settles that,
 * and checking it should not mean opening a second tab and losing the field
 * list.
 */
function FieldsTab({
  data,
  compare,
  onToggleCompare,
  highlight,
  onClearHighlight,
}: {
  data: Detail;
  compare: boolean;
  onToggleCompare: () => void;
  /** Field name from ?field=, arrived at from the agent's field drill-down. */
  highlight: string | null;
  onClearHighlight: () => void;
}) {
  const hit = useRef<HTMLTableRowElement | null>(null);

  // Long field lists scroll, so landing on the page is not the same as seeing
  // the row that was linked to.
  useEffect(() => {
    if (highlight && hit.current) {
      hit.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [highlight, data.document_id]);

  if (!data.fields?.length) {
    return (
      <Empty
        title="No field-level extraction stored"
        hint="This document has raw OCR text but no agent extraction, so there are no per-field confidences to show."
      />
    );
  }

  // Fields can come from the agent (with confidence) or from the local text
  // parser (which reports none). Saying which avoids the em dashes reading as
  // a bug when the whole column is empty.
  const parserSource = data.fields.find(
    (f) => f.source && f.source !== "agent_results",
  )?.source;

  return (
    <>
      {parserSource ? (
        <div className="notice notice-warn">
          <strong>No per-field confidence for this extraction.</strong> These
          fields came from the <span className="mono">{parserSource}</span>{" "}
          regex path in ppd_preprocessor, not from an AI agent, and a regex
          reports no confidence — so the column is blank throughout rather than
          filled with a number nobody measured.
          <div style={{ marginTop: 8 }}>
            This document was extracted while the Azure provider was returning{" "}
            <span className="mono">400</span> on every call, which forced the
            fallback. That is fixed, so resubmitting it now runs the real agent
            and returns a confidence per field:
            <div className="mono" style={{ marginTop: 6 }}>
              POST /api/orchestrator/documents/submit — same storage_key, with{" "}
              <strong>force_reprocess: true</strong>
            </div>
          </div>
        </div>
      ) : null}

      {highlight ? (
        <div className="notice">
          {data.fields.some((f) => f.field === highlight) ? (
            <>
              Showing <strong>{humanise(highlight)}</strong> in context — the
              row is highlighted below. Compare it against the scan to confirm
              the reading.
            </>
          ) : (
            <>
              <strong>{humanise(highlight)}</strong> is not in this
              document&rsquo;s extraction, so there is no row to highlight. The
              agent aggregate counts a run once the field appears in it; this
              run stored a different set.
            </>
          )}
          <button style={{ marginLeft: 10 }} onClick={onClearHighlight}>
            Clear
          </button>
        </div>
      ) : null}

      <div className="compare-bar">
        <span className="muted">
          {data.fields.length} field{data.fields.length === 1 ? "" : "s"}{" "}
          extracted
        </span>
        <button
          className={compare ? "btn-primary" : ""}
          onClick={onToggleCompare}
          aria-pressed={compare}
        >
          {compare ? "Hide document" : "Compare with document"}
        </button>
      </div>

      <div className={compare ? "compare-split" : undefined}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col" className="si">
                  Sl
                </th>
                <th scope="col">Field</th>
                <th scope="col">Value</th>
                <th scope="col">Confidence</th>
                {/* Dropped while comparing: the split halves the width, and the
                    normalised value is the least useful column when the point
                    is reading the raw value against the page. */}
                {compare ? null : <th scope="col">Normalised</th>}
              </tr>
            </thead>
            <tbody>
              {data.fields.map((f, i) => (
                <tr
                  key={f.field}
                  ref={f.field === highlight ? hit : undefined}
                  className={f.field === highlight ? "row-hit" : undefined}
                >
                  <td className="si">{i + 1}</td>
                  <td className="nowrap">{humanise(f.field)}</td>
                  <td className="value-cell">{fmtValue(f.value)}</td>
                  <td>
                    <ConfidenceBadge
                      value={f.confidence}
                      absentReason="The extraction reported no confidence for this field"
                    />
                  </td>
                  {compare ? null : (
                    <td className="value-cell muted">
                      {fmtValue(f.normalized)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {compare ? (
          <DocumentViewer
            src={documentFilePath(data.document_id)}
            filename={data.original_filename}
            height={640}
          />
        ) : null}
      </div>
    </>
  );
}

/**
 * Raw OCR text with line numbers.
 *
 * There is no per-line confidence column here on purpose. The OCR path is a
 * vision chat model (or a local text-layer read), and neither returns a
 * per-line score -- so a confidence column could only be filled by inventing
 * numbers. The per-field confidences on the first tab are the real, reported
 * scores; see the note at the foot of this tab.
 */
function RawTab({ data }: { data: Detail }) {
  const text = data.raw_ocr_text?.raw_text ?? "";
  if (!text) return <Empty title="No OCR text stored for this document" />;

  const lines = text.split("\n");

  return (
    <>
      <div className="controls" style={{ marginBottom: 12 }}>
        <span className="muted">
          {lines.length} lines · {data.raw_ocr_text?.pages ?? EM_DASH} page(s) ·{" "}
          engine {data.ocr_engine ?? EM_DASH}
        </span>
      </div>

      <pre className="raw-text">
        {lines.map((line, i) => (
          <div key={i}>
            <span className="line-no">{i + 1}</span>
            {line}
          </div>
        ))}
      </pre>

      <div className="notice" style={{ marginTop: 16, marginBottom: 0 }}>
        <strong>No per-line confidence exists for this document.</strong>{" "}
        Scanned pages are transcribed by a vision chat model and digital PDFs
        are read from their embedded text layer; neither returns a per-line
        score. Rather than show a number that was never measured, the per-field
        confidences on the <em>Extracted fields</em> tab are the real reported
        scores. Genuine per-line confidence would need Azure Document
        Intelligence (<span className="mono">prebuilt-read</span>) or Tesseract
        word data.
      </div>
    </>
  );
}

function VerdictTab({
  data,
  gate,
}: {
  data: Detail;
  gate: Record<string, unknown> | null;
}) {
  return (
    <dl className="kv">
      <dt>Job status</dt>
      <dd>{humanise(data.status)}</dd>

      <dt>Reason code</dt>
      <dd>{data.reason_code ?? EM_DASH}</dd>

      <dt>Gate action</dt>
      <dd>
        {gate?.gate_action ? humanise(String(gate.gate_action)) : EM_DASH}
      </dd>

      <dt>Gate confidence</dt>
      <dd>
        <ConfidenceBadge
          value={(gate?.confidence_score as number | undefined) ?? null}
        />
      </dd>

      <dt>Deterministic checks</dt>
      <dd>
        {(gate?.passed_deterministic_checks as string | undefined) ?? EM_DASH}
      </dd>

      <dt>Review required</dt>
      <dd>
        {gate?.review_required === undefined
          ? EM_DASH
          : gate.review_required
            ? "Yes"
            : "No"}
      </dd>

      <dt>Extraction source</dt>
      <dd className="mono">
        {(data.extracted_data?.extraction_source as string | undefined) ??
          EM_DASH}
      </dd>

      <dt>Processing path</dt>
      <dd className="mono">
        {(data.extracted_data?.processing_path as string | undefined) ??
          EM_DASH}
      </dd>

      <dt>Storage</dt>
      <dd className="mono">
        {data.storage_provider ?? EM_DASH} / {data.storage_key ?? EM_DASH}
      </dd>

      <dt>File hash</dt>
      <dd className="mono">{data.file_hash ?? EM_DASH}</dd>

      <dt>Tenant</dt>
      <dd>{data.tenant_id ?? EM_DASH}</dd>
    </dl>
  );
}
